// Thunks extracted from PusherModule.js

import _ from "lodash";
import {
  getTotalCommentOfNode,
  commentParentTypeToEntityMapping,
} from "../Services";
import { getPlannerFieldsOfNode } from "UnitPlans/modules/UnitPlanModule";
import { getUnitPlanCollaboratorsDetails } from "IBPlanner/modules/IBPlannerModule";
import { PusherEvent, PresenceEvent, BlurFieldEvent } from "Constants";
import { getConversations } from "ChatContainer/modules/ChatContainerModule";
import {
  getLatestMessageStateIfNoMutations,
  handleMessageReadV2,
  handleMessagesChanges,
  handleMessagesRead,
  handleNewMessage,
} from "MessagingV2/modules/MessagingModule";
import { isMessagingV2Enabled } from "MessagingV2/modules/Utils";
import { getUnitPlanFieldResponseFilter } from "UnitPlans/modules/Utils";
import { connectionManager } from "lib/ablyInstance";
import { initLockedFields, handleFocuedField } from "./PusherActions";
import {
  PUSHER_EVENTS_MAPPING,
  getChannelName,
  getLockedFieldsFromMembers,
  getMembersOfPresenceChannel,
} from "./PusherUtils";
import {
  TYPE_TO_PRESENCE_EVENTS_MAPPING,
  TYPE_TO_BLUR_FIELD_EVENTS_MAPPING,
} from "./PusherConstants";

// Thunks
export const toggleFocusedField = data => {
  return (dispatch, getState) => {
    const { type, focusedField = {}, isFocused } = data;
    const lockedFields = getState().pusher.lockedFields[type];
    const lockedField = _.find(
      lockedFields,
      field =>
        field.id == _.get(focusedField, "id", "") &&
        _.get(field, "uid") == _.get(focusedField, "uid", "")
    );
    if (!lockedField) {
      const event = TYPE_TO_PRESENCE_EVENTS_MAPPING[type];

      if (event) {
        PresenceEvent.emit(event, { isFocused, focusedField });
      }
      dispatch(handleFocuedField(data));
    }
  };
};

///Normal channel
const bindPusherEvent = ({
  eventData: { metadata = "{}", x_tod_lsn: xTodLsn },
  events,
  data,
  eventName,
}) => {
  return (dispatch, getState) => {
    const {
      authorId,
      fields,
      parentId,
      parentType,
      conversation_id,
      message_id: messageId,
    } = JSON.parse(metadata) || {};

    if (xTodLsn) {
      localStorage.setItem("x-tod-lsn", xTodLsn);
    }

    const { unitPlanId, leId, assessmentId } = data;

    if (!(!!authorId && authorId == data.userId)) {
      _.map(events, event => {
        switch (event) {
          case "nodeTotalCommentUpdate":
            dispatch(
              getTotalCommentOfNode({
                id: parentId,
                type: commentParentTypeToEntityMapping(parentType),
              })
            );
            break;
          case "chatMessageReceived":
            if (isMessagingV2Enabled()) {
              const parsedData = JSON.parse(metadata) || {};
              const { chatId, chatType } = parsedData;

              // if chatId is not present, it means this is not a new message for a messagingV2
              if (chatId) {
                dispatch(handleMessagesChanges({ chatId, messageId }));
                dispatch(handleNewMessage({ chatId, chatType }));
              }
            }

            // in family messaging we are using parentId for chatMessageReceived notification which might not be present
            //  in case of messagingV2 or other notifications so we are checking for parentId
            if (parentId) {
              dispatch(
                getConversations({
                  parentId,
                  parentType,
                  conversationId: conversation_id,
                })
              );
            }
            PusherEvent.emit(event, { event, ...(JSON.parse(metadata) || {}) });
            break;
          case "chatMessageV2ReadReceipt":
            if (isMessagingV2Enabled()) {
              const { chatId } = JSON.parse(metadata) || {};
              handleMessagesRead({
                chatId,
              });
            }
            PusherEvent.emit(event, { event, ...(JSON.parse(metadata) || {}) });
            break;
          case "chatMessageV3ReadReceipt":
            {
              const { chatId, maxMessageId } = JSON.parse(metadata) || {};
              handleMessageReadV2({
                chatId,
                maxMessageId,
              });
            }
            PusherEvent.emit(event, { event, ...(JSON.parse(metadata) || {}) });
            break;
          case "chatMessageToggleReaction":
            if (isMessagingV2Enabled()) {
              const { chatId, notification_type: notificationType } =
                JSON.parse(metadata) || {};

              const scrollChatId =
                notificationType === "MESSAGE_REACTION_ADDED"
                  ? chatId
                  : undefined;

              getLatestMessageStateIfNoMutations({
                messageId,
                scrollChatId,
              });
            }
            break;
          case "unitPlanFieldUpdate": {
            const responseFilter = getUnitPlanFieldResponseFilter();
            dispatch(
              getPlannerFieldsOfNode({
                type: "UNIT_PLAN",
                uids: fields,
                id: unitPlanId,
                responseFilter,
              })
            );
            PusherEvent.emit(event, { event, ...(JSON.parse(metadata) || {}) });
            break;
          }
          case "unitPlanCollaboratorUpdate":
            dispatch(getUnitPlanCollaboratorsDetails({ id: unitPlanId }));
            break;
          case "leFieldUpdate":
            dispatch(
              getPlannerFieldsOfNode({
                type: "LEARNING_ENGAGEMENT",
                uids: fields,
                id: leId,
              })
            );
            break;
          case "assessmentFieldUpdate":
            dispatch(
              getPlannerFieldsOfNode({
                type: "ASSESSMENT",
                uids: fields,
                id: assessmentId,
              })
            );
            break;
          default:
            PusherEvent.emit(event, { event, ...(JSON.parse(metadata) || {}) });
        }
      });
    }
  };
};

export const subscribeToChannel = ({ data = {}, channelType }) => {
  return async (dispatch, getState) => {
    const conn = connectionManager.getConnection();
    if (!conn) {
      return;
    }

    const channelName = getChannelName({ data, channelType });
    const channel = conn.channels.get(channelName);

    channel.subscribe(message => {
      const eventData = message.data;
      const eventName = message.name;
      const events = PUSHER_EVENTS_MAPPING[eventName];

      dispatch(bindPusherEvent({ eventData, events, eventName, data }));
    });
  };
};

export const unSubscribeToChannel = ({ data = {}, channelType }) => {
  return async (dispatch, getState) => {
    const conn = connectionManager.getConnection();
    if (!conn) {
      return;
    }
    const channelName = getChannelName({ data, channelType });
    const channel = conn.channels.get(channelName);
    channel.detach();
  };
};

/////Presence Channel
export const bindPresenceEvents = ({ channelType, message, channel }) => {
  return (dispatch, getState) => {
    let members = [];
    if (!channel) {
      return;
    }

    let lockedFields = [];

    // const focusedField = getState().pusher.focusedField[channelType];
    // console.log("Previously Focused Field", focusedField);
    // if (focusedField) {
    //   BlurFieldEvent.emit(TYPE_TO_BLUR_FIELD_EVENTS_MAPPING[channelType], {
    //     field: focusedField
    //   });
    // }

    switch (message.action) {
      case "enter":
        if (
          message.connectionId ==
          _.get(connectionManager.getConnection(), "connection.id", "")
        ) {
          members = getMembersOfPresenceChannel({ channel });

          lockedFields = getLockedFieldsFromMembers(members);
          dispatch(initLockedFields({ type: channelType, lockedFields }));
          //console.log(lockedFields, "locked Field", message.action);
          const focusedField = getState().pusher.focusedField[channelType];

          // console.log("Previously Focused Field", focusedField);

          if (focusedField) {
            BlurFieldEvent.emit(
              TYPE_TO_BLUR_FIELD_EVENTS_MAPPING[channelType],
              {
                field: focusedField,
              }
            );
          }
          //dispatch(toggleFocusedField({ isFocused: false, type: channelType }));
        }

        break;
      case "leave":
      case "update":
        if (
          message.connectionId !=
          _.get(connectionManager.getConnection(), "connection.id", "")
        ) {
          members = getMembersOfPresenceChannel({ channel });

          lockedFields = getLockedFieldsFromMembers(members);
          dispatch(initLockedFields({ type: channelType, lockedFields }));
          //console.log(lockedFields, "locked Field", message.action);
        }
    }
  };
};

export const updateStateToPresenceChannel = ({ channel, lockedField }) => {
  return (dispatch, getState) => {
    if (!channel) {
      return;
    }
    const {
      id,
      first_name: firstName,
      last_name: lastName,
      profile_image: profileImage,
    } = getState().login.userInfo;
    channel.presence.update({
      userInfo: { id, firstName, lastName, profileImage },
      lockedField,
    });
  };
};

export const enterPresenceChannel = ({ channel }) => {
  return (dispatch, getState) => {
    if (!channel) {
      return;
    }
    const {
      id,
      first_name: firstName,
      last_name: lastName,
      profile_image: profileImage,
    } = getState().login.userInfo;
    channel.presence.enter({
      userInfo: { id, firstName, lastName, profileImage },
      // lockedField: { uid: "title" }
    });
  };
};

export const subscribeToPresenceChannel = ({ channel, channelType }) => {
  return (dispatch, getState) => {
    if (!channel) {
      return;
    }
    dispatch(enterPresenceChannel({ channelType, channel }));

    channel.presence.subscribe(["enter", "leave", "update"], message => {
      dispatch(bindPresenceEvents({ message, channelType, channel }));
    });
  };
};

export const unSubscribeToPresenceChannel = ({ channel }) => {
  return (dispatch, getState) => {
    if (!channel) {
      return;
    }
    channel.presence.leave();
    channel.unsubscribe();
  };
};

// Auto-generated exports
export { bindPusherEvent };
